/**
 * Dashboard page-level access for company members (replaces buyer/seller hub groupings).
 */

export const DASHBOARD_PAGE_KEYS = {
  MARKETPLACE: 'MARKETPLACE',
  WALLET_DASHBOARD: 'WALLET_DASHBOARD',
  ORDER_REQUEST: 'ORDER_REQUEST',
  LIST_PRODUCTS: 'LIST_PRODUCTS',
  TRACK_UPDATE_DELIVERY: 'TRACK_UPDATE_DELIVERY',
  WISHLIST: 'WISHLIST',
  NOTIFICATIONS: 'NOTIFICATIONS',
  CART: 'CART',
  PROFILE: 'PROFILE',
  USER_MANAGEMENT: 'USER_MANAGEMENT',
};

/** Display order in Add/Edit member forms */
export const DASHBOARD_PAGE_OPTIONS = [
  {
    access: DASHBOARD_PAGE_KEYS.MARKETPLACE,
    name: 'Marketplace',
    roles: [
      'ADMIN',
      'FINANCE',
      'LEGAL',
      'MANAGER',
      'DASHBOARD VIEWER',
      'OPERATIONS',
      'PLANNING AND PROCUREMENT',
      'R AND D',
      'EXECUTIVE',
      'BUSINESS DEVELOPMENT',
      'MARKETING',
    ],
  },
  {
    access: DASHBOARD_PAGE_KEYS.WALLET_DASHBOARD,
    name: 'Wallet Dashboard',
    roles: ['ADMIN', 'FINANCE', 'LEGAL', 'MANAGER', 'DASHBOARD VIEWER'],
  },
  {
    access: DASHBOARD_PAGE_KEYS.ORDER_REQUEST,
    name: 'Order Request',
    roles: [
      'ADMIN',
      'OPERATIONS',
      'PLANNING AND PROCUREMENT',
      'FINANCE',
      'R AND D',
      'LEGAL',
      'MANAGER',
      'EXECUTIVE',
      'BUSINESS DEVELOPMENT',
      'MARKETING',
    ],
  },
  {
    access: DASHBOARD_PAGE_KEYS.LIST_PRODUCTS,
    name: 'List Products',
    roles: [
      'ADMIN',
      'BUSINESS DEVELOPMENT',
      'MARKETING',
      'OPERATIONS',
      'FINANCE',
      'R AND D',
      'LEGAL',
      'MANAGER',
      'EXECUTIVE',
    ],
  },
  {
    access: DASHBOARD_PAGE_KEYS.TRACK_UPDATE_DELIVERY,
    name: 'Track/Update Delivery',
    roles: [
      'ADMIN',
      'OPERATIONS',
      'PLANNING AND PROCUREMENT',
      'R AND D',
      'MANAGER',
      'EXECUTIVE',
      'BUSINESS DEVELOPMENT',
      'MARKETING',
    ],
  },
  {
    access: DASHBOARD_PAGE_KEYS.WISHLIST,
    name: 'Wishlist',
    roles: [
      'ADMIN',
      'OPERATIONS',
      'PLANNING AND PROCUREMENT',
      'R AND D',
      'MANAGER',
      'EXECUTIVE',
    ],
  },
  {
    access: DASHBOARD_PAGE_KEYS.NOTIFICATIONS,
    name: 'Notifications',
    roles: [
      'ADMIN',
      'OPERATIONS',
      'PLANNING AND PROCUREMENT',
      'FINANCE',
      'R AND D',
      'LEGAL',
      'MANAGER',
      'EXECUTIVE',
      'DASHBOARD VIEWER',
      'BUSINESS DEVELOPMENT',
      'MARKETING',
    ],
  },
  {
    access: DASHBOARD_PAGE_KEYS.CART,
    name: 'Cart',
    roles: [
      'ADMIN',
      'OPERATIONS',
      'PLANNING AND PROCUREMENT',
      'R AND D',
      'LEGAL',
      'MANAGER',
      'EXECUTIVE',
      'BUSINESS DEVELOPMENT',
      'MARKETING',
    ],
  },
  {
    access: DASHBOARD_PAGE_KEYS.PROFILE,
    name: 'Profile',
    roles: [
      'ADMIN',
      'OPERATIONS',
      'PLANNING AND PROCUREMENT',
      'FINANCE',
      'R AND D',
      'LEGAL',
      'MANAGER',
      'EXECUTIVE',
      'DASHBOARD VIEWER',
      'BUSINESS DEVELOPMENT',
      'MARKETING',
    ],
  },
  {
    access: DASHBOARD_PAGE_KEYS.USER_MANAGEMENT,
    name: 'User Management',
    roles: ['ADMIN', 'MANAGER'],
  },
];

const LEGACY_TO_PAGE = {
  MY_ORDERS: DASHBOARD_PAGE_KEYS.ORDER_REQUEST,
  ORDERS: DASHBOARD_PAGE_KEYS.ORDER_REQUEST,
  PRODUCTS_UPLOAD: DASHBOARD_PAGE_KEYS.LIST_PRODUCTS,
  UPDATE_YOUR_SALES: DASHBOARD_PAGE_KEYS.TRACK_UPDATE_DELIVERY,
  TRACK_YOUR_PURCHASES: DASHBOARD_PAGE_KEYS.TRACK_UPDATE_DELIVERY,
  WALLET_DASHBOARD: DASHBOARD_PAGE_KEYS.WALLET_DASHBOARD,
  WISHLIST: DASHBOARD_PAGE_KEYS.WISHLIST,
  NOTIFICATIONS: DASHBOARD_PAGE_KEYS.NOTIFICATIONS,
  CART: DASHBOARD_PAGE_KEYS.CART,
  PROFILE_VIEW: DASHBOARD_PAGE_KEYS.PROFILE,
  PROFILE_VIEW_AND_EDIT: DASHBOARD_PAGE_KEYS.PROFILE,
  MARKETPLACE: DASHBOARD_PAGE_KEYS.MARKETPLACE,
};

const PAGE_TO_LEGACY_BUYER = {
  [DASHBOARD_PAGE_KEYS.ORDER_REQUEST]: ['MY_ORDERS'],
  [DASHBOARD_PAGE_KEYS.TRACK_UPDATE_DELIVERY]: ['TRACK_YOUR_PURCHASES'],
  [DASHBOARD_PAGE_KEYS.WALLET_DASHBOARD]: ['WALLET_DASHBOARD'],
  [DASHBOARD_PAGE_KEYS.WISHLIST]: ['WISHLIST'],
  [DASHBOARD_PAGE_KEYS.NOTIFICATIONS]: ['NOTIFICATIONS'],
  [DASHBOARD_PAGE_KEYS.CART]: ['CART'],
  [DASHBOARD_PAGE_KEYS.MARKETPLACE]: [],
};

const PAGE_TO_LEGACY_SELLER = {
  [DASHBOARD_PAGE_KEYS.ORDER_REQUEST]: ['ORDERS'],
  [DASHBOARD_PAGE_KEYS.LIST_PRODUCTS]: ['PRODUCTS_UPLOAD'],
  [DASHBOARD_PAGE_KEYS.TRACK_UPDATE_DELIVERY]: ['UPDATE_YOUR_SALES'],
  [DASHBOARD_PAGE_KEYS.WALLET_DASHBOARD]: ['WALLET_DASHBOARD'],
  [DASHBOARD_PAGE_KEYS.NOTIFICATIONS]: ['NOTIFICATIONS'],
  [DASHBOARD_PAGE_KEYS.CART]: ['CART'],
  [DASHBOARD_PAGE_KEYS.MARKETPLACE]: [],
};

/** Route prefixes guarded per page (longest match wins in guard) */
export const PAGE_ROUTE_PREFIXES = {
  [DASHBOARD_PAGE_KEYS.MARKETPLACE]: [
    '/home/search',
    '/home/brand-products',
    '/home/ready-stock',
    '/home/bulk-purchase',
    '/home/smart-bulk-purchase',
    '/home/hotel-planner',
    '/home/mediabuying',
    '/home/multiplexmediabuying',
    '/home/digitalAndHoardingMediaBuying',
    '/home/wishlistproductdetail',
    '/home/wishlistmediabuying',
    '/home/wishlistvoucherdetail',
    '/home/voucherdetail',
    '/home/giftandvaluevoucher',
    '/home/electronicsdetail',
    '/home/textilesvoucherprev',
    '/home/valueandgiftvoucher',
    '/home/spacificvoucher',
    '/home/product',
    '/home/productanalytics',
    '/home/ai-media-planner',
    '/home/htmltopdf',
    '/home/conditions',
    '/home/help-and-feedback',
    '/home/membershipdetail',
    '/home/loader',
  ],
  [DASHBOARD_PAGE_KEYS.WALLET_DASHBOARD]: ['/home/dashboard', '/home/wallet'],
  [DASHBOARD_PAGE_KEYS.ORDER_REQUEST]: [
    '/home/order-requests',
    '/home/order-request',
    '/home/sellerorderlist',
    '/home/myorderlist',
    '/home/voucherorderlist',
    '/home/sellervoucherorderlist',
    '/home/ordersummerydetails',
    '/home/Voucherordersummerydetailspage',
    '/home/mediaordersummerydetails',
    '/home/purchaseorderdetails',
    '/home/mediapurchaseorderdetails',
    '/home/mediapurchaseorderdetailsseller',
    '/home/purchaseorder',
    '/home/proformainvoice',
    '/home/proformaInvoice',
    '/home/performainvoice',
    '/home/sellerperformainvoice',
    '/home/invoice',
    '/home/taxinvoice',
    '/home/shippinginvoice',
    '/home/vouchertaxinvoice',
    '/home/voucherproformainvoice',
    '/home/mediataxinvoice',
    '/home/commission',
    '/home/bscommission',
    '/home/commissionByOrderId',
    '/home/commissionlist',
    '/home/bw-tds-debit-note',
  ],
  [DASHBOARD_PAGE_KEYS.LIST_PRODUCTS]: [],
  [DASHBOARD_PAGE_KEYS.TRACK_UPDATE_DELIVERY]: [
    '/home/trackorders',
  ],
  [DASHBOARD_PAGE_KEYS.WISHLIST]: ['/home/wishlist'],
  [DASHBOARD_PAGE_KEYS.NOTIFICATIONS]: ['/home/fullscreennotification'],
  [DASHBOARD_PAGE_KEYS.CART]: ['/home/cart'],
  [DASHBOARD_PAGE_KEYS.PROFILE]: ['/home/profile'],
  [DASHBOARD_PAGE_KEYS.USER_MANAGEMENT]: [
    '/home/company_members',
    '/home/add_member',
    '/home/addmember',
    '/home/edit_member',
    '/home/deleted_members',
    '/home/planpricing',
  ],
};

/** Paths always allowed for any logged-in dashboard user */
export const UNGUARDED_HOME_PREFIXES = [];

export const PUBLIC_BROWSE_PREFIXES = [
  '/explore',
  '/calculator',
  '/home/search',
  '/home/brand-products',
  '/home/ready-stock',
  '/home/marketplace/category',
  '/home/marketplaceproductlist',
  '/home/marketplaceproductpreview',
  '/home/productdetail',
  '/home/voucherdetail',
  '/home/giftandvaluevoucher',
  '/home/electronicsdetail',
  '/home/mediabuying',
  '/home/multiplexmediabuying',
  '/home/digitalAndHoardingMediaBuying',
];

/**
 * Juspay membership return pages. The user lands here with ?membership_order=<id>
 * straight from the gateway and the page finalizes the plan (poll -> create -> poll).
 */
export const MEMBERSHIP_FINALIZE_PREFIXES = [
  '/home/membershipfeesinvoice',
  '/home/membershiprenewalinvoice',
];

export function isMembershipFinalizingPath(pathname, search) {
  if (!pathname) return false;
  const onFinalizePage = MEMBERSHIP_FINALIZE_PREFIXES.some((prefix) =>
    pathMatchesPrefix(pathname, prefix),
  );
  if (!onFinalizePage) return false;
  return new URLSearchParams(search || '').has('membership_order');
}

/** True if `pathname` is guest-viewable without authentication. */
export function isPublicBrowsePath(pathname) {
  if (!pathname) return false;
  // Marketplace landing (index) — exact match only, not every /home/* page.
  if (pathname === '/home' || pathname === '/home/') return true;
  return PUBLIC_BROWSE_PREFIXES.some((prefix) => pathMatchesPrefix(pathname, prefix));
}

export function getDefaultPageAccessForRole(roleName) {
  if (!roleName) return [];
  return DASHBOARD_PAGE_OPTIONS.filter((item) =>
    item.roles.includes(roleName),
  ).map((item) => item.access);
}

export function normalizePageKey(key) {
  if (!key) return null;
  return LEGACY_TO_PAGE[key] || key;
}

export function getSelectedPageAccessList(rightsarray) {
  if (!rightsarray || typeof rightsarray !== 'object') return [];
  const fromPages = Array.isArray(rightsarray.PAGES) ? rightsarray.PAGES : [];
  const fromBuyer = Array.isArray(rightsarray.BUYER) ? rightsarray.BUYER : [];
  const fromSeller = Array.isArray(rightsarray.SELLER) ? rightsarray.SELLER : [];
  const merged = [...fromPages, ...fromBuyer, ...fromSeller]
    .map(normalizePageKey)
    .filter(Boolean);
  const pages = [...new Set(merged)];
  // Legacy members (BUYER/SELLER only) did not store MARKETPLACE; keep browse access.
  const hasLegacyOnly =
    fromPages.length === 0 &&
    (fromBuyer.length > 0 || fromSeller.length > 0);
  if (
    hasLegacyOnly &&
    pages.length > 0 &&
    !pages.includes(DASHBOARD_PAGE_KEYS.MARKETPLACE)
  ) {
    pages.unshift(DASHBOARD_PAGE_KEYS.MARKETPLACE);
  }
  return pages;
}

export function hasDashboardPageAccess(rightsarray, pageKey, superAdmin = false) {
  if (superAdmin) return true;
  const normalizedKey = normalizePageKey(pageKey);
  const selected = getSelectedPageAccessList(rightsarray);
  return selected.includes(normalizedKey);
}

export function buildRightsarrayPayload(selectedPages = []) {
  const pages = [...new Set((selectedPages || []).map(normalizePageKey).filter(Boolean))];
  const buyer = [];
  const seller = [];
  pages.forEach((page) => {
    (PAGE_TO_LEGACY_BUYER[page] || []).forEach((k) => {
      if (!buyer.includes(k)) buyer.push(k);
    });
    (PAGE_TO_LEGACY_SELLER[page] || []).forEach((k) => {
      if (!seller.includes(k)) seller.push(k);
    });
  });
  return { PAGES: pages, BUYER: buyer, SELLER: seller };
}

export function rightsarrayToSelectedPages(rightsarray) {
  return getSelectedPageAccessList(rightsarray);
}

function pathMatchesPrefix(pathname, prefix) {
  if (pathname === prefix) return true;
  return pathname.startsWith(`${prefix}/`);
}

export function resolvePageKeyForPath(pathname) {
  if (!pathname || !pathname.startsWith('/home')) return null;
  if (pathname === '/home' || pathname === '/home/') {
    return DASHBOARD_PAGE_KEYS.MARKETPLACE;
  }
  if (UNGUARDED_HOME_PREFIXES.some((p) => pathMatchesPrefix(pathname, p))) {
    return null;
  }

  let matchedPage = null;
  let matchedLen = 0;
  Object.entries(PAGE_ROUTE_PREFIXES).forEach(([pageKey, prefixes]) => {
    prefixes.forEach((prefix) => {
      if (pathMatchesPrefix(pathname, prefix) && prefix.length > matchedLen) {
        matchedPage = pageKey;
        matchedLen = prefix.length;
      }
    });
  });
  return matchedPage;
}

export function isPathAllowedForUser(pathname, rightsarray, superAdmin = false) {
  if (superAdmin) return true;
  const pageKey = resolvePageKeyForPath(pathname);
  if (!pageKey) return true;
  return hasDashboardPageAccess(rightsarray, pageKey, false);
}

export function getFirstAllowedHomePath(rightsarray, superAdmin = false) {
  if (superAdmin) return '/home';
  const selected = getSelectedPageAccessList(rightsarray);
  const order = [
    DASHBOARD_PAGE_KEYS.MARKETPLACE,
    DASHBOARD_PAGE_KEYS.WALLET_DASHBOARD,
    DASHBOARD_PAGE_KEYS.ORDER_REQUEST,
    DASHBOARD_PAGE_KEYS.LIST_PRODUCTS,
    DASHBOARD_PAGE_KEYS.TRACK_UPDATE_DELIVERY,
    DASHBOARD_PAGE_KEYS.WISHLIST,
    DASHBOARD_PAGE_KEYS.NOTIFICATIONS,
    DASHBOARD_PAGE_KEYS.CART,
    DASHBOARD_PAGE_KEYS.PROFILE,
  ];
  const pathByPage = {
    [DASHBOARD_PAGE_KEYS.MARKETPLACE]: '/home',
    [DASHBOARD_PAGE_KEYS.WALLET_DASHBOARD]: '/home/dashboard',
    [DASHBOARD_PAGE_KEYS.ORDER_REQUEST]: '/home/order-requests',
    [DASHBOARD_PAGE_KEYS.LIST_PRODUCTS]: '/home',
    [DASHBOARD_PAGE_KEYS.TRACK_UPDATE_DELIVERY]: '/home/trackorders',
    [DASHBOARD_PAGE_KEYS.WISHLIST]: '/home/wishlist',
    [DASHBOARD_PAGE_KEYS.NOTIFICATIONS]: '/home/fullscreennotification',
    [DASHBOARD_PAGE_KEYS.CART]: '/home/cart',
    [DASHBOARD_PAGE_KEYS.PROFILE]: '/home/profile',
  };
  for (const key of order) {
    if (selected.includes(key)) return pathByPage[key];
  }
  return '/home';
}

export function hasLegacyCartAccess(rightsarray, superAdmin = false) {
  return hasDashboardPageAccess(rightsarray, DASHBOARD_PAGE_KEYS.CART, superAdmin);
}
